import { useState } from 'react'
import { ApiError, api } from '../lib/api.js'

/**
 * One button, three states, and a fourth that is not a button at all.
 *
 * `null` → Follow · `pending` → Requested · `accepted` → Following. The
 * pending state exists because a private account decides for itself, and a
 * button that said "Following" while a request sat unanswered would be the
 * interface lying about a permission.
 *
 * When they have blocked you there is no button. A control that cannot work
 * is worse than no control — it invites the click that produces the error.
 */
const LABELS = {
  null: 'Follow',
  pending: 'Requested',
  accepted: 'Following',
}

export default function FollowButton({ handle, state, blockedMe = false, onChange }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  if (blockedMe) return null

  const key = state === null || state === undefined ? 'null' : state
  const label = LABELS[key] || 'Follow'
  const following = key !== 'null'

  async function toggle() {
    setBusy(true)
    setError('')
    try {
      const next = following ? await api.unfollow(handle) : await api.follow(handle)
      onChange?.(next)
    } catch (err) {
      // Names which of the two things it was doing. One control does both,
      // so a single vague sentence would be about neither.
      const fallback = following ? 'Could not unfollow just now.' : 'Could not follow just now.'
      setError(err instanceof ApiError ? err.message : fallback)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="follow-control">
      <button
        type="button"
        className={`btn btn-sm ${following ? 'btn-quiet' : 'btn-primary'}`}
        onClick={toggle}
        disabled={busy}
        // The accessible name says what pressing it *does*, which is the
        // opposite of what the visible label says once you follow somebody.
        aria-label={following ? `Unfollow ${handle}` : `Follow ${handle}`}
      >
        {busy ? '…' : label}
      </button>
      {error ? (
        <p className="footnote error mb-0" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}
